import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Award, DollarSign, ExternalLink, MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import RichContent, { normalizeRichText, richTextIsEmpty } from '../../../components/RichContent';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import EndorsementModal from '../../components/endorsements/EndorsementModal';
import EndorsementList from '../../components/endorsements/EndorsementList';
import { endorserDisplayName, type EndorsementInput } from '../../../constants/endorsements';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import HelpTrigger from '../../../help/components/HelpTrigger';
import { WarningIcon } from '../../../components/icons';
import {
  searchImportedCommittees,
  financeCoIdOf,
} from '../../lib/pickers';
import { ARGUMENT_SOURCE_TYPES, INITIATIVE_SIDES } from '../../../constants/elections';
import { COMMITTEE_TYPES, committeeTypeLabel } from '../../../constants/ballotInitiatives';
import {
  apiError,
  inputClasses,
  labelClasses,
  InitiativeDetail,
  InitiativeCommittee,
  InitiativeEndorsement,
  LinkedMeasure,
  SideFinance,
} from './InitiativeDetailPage';
import {
  CommitteeLinks,
  committeeLinkWarnings,
  emptyTotalsReason,
  measureLabel,
  sideCountParts,
} from './sideFinanceText';

interface Props {
  initiative: InitiativeDetail;
  onChanged: () => void;
}

/** Neutral committees are registered but unaligned — neither side's colour. */
const SIDE_CHIP_VARIANTS: Record<string, 'success' | 'danger' | 'neutral'> = {
  proponent: 'success',
  opponent: 'danger',
  neutral: 'neutral',
};

/**
 * A measure's committee link holds only the committee, its side and notes about
 * this measure; name, purpose, website, organization and contact belong to the
 * committee and are edited in the Committees tool (Campaign Committee Registry
 * PRD §5.4). `match_name` pre-searches the picker when matching a legacy row.
 */
const EMPTY_COMMITTEE_FORM = {
  id: '', committee_type: 'proponent', notes: '', match_name: '',
};

const committeeToolPath = (coId: string) => `/admin-next/committees/${encodeURIComponent(coId)}`;

const SIDE_BY_COMMITTEE_TYPE: Record<string, 'support' | 'oppose'> = { proponent: 'support', opponent: 'oppose' };
const EMPTY_ARGUMENT_FORM = { side: 'support', source_type: 'other', title: '', body: '', source_name: '', source_url: '' };
type DeleteTarget =
  | { kind: 'committee'; id: string; label: string }
  | { kind: 'argument'; id: string; label: string }
  | { kind: 'endorsement'; id: string; label: string };

const fmtMoney = (amount: number) =>
  `$${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/** A committee's purpose statement, clamped to two lines with a More/Less toggle (§5.8). */
const PurposeText: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const long = text.length > 160;
  return (
    <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
      <p className={expanded || !long ? '' : 'line-clamp-2'}>
        <span className="text-text-tertiary dark:text-text-dark-tertiary">Purpose: </span>
        {text}
      </p>
      {long && (
        <Button variant="ghost" size="sm" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Less' : 'More'}
        </Button>
      )}
    </div>
  );
};

/** Other measures, as links to their admin pages: "#108 Name (supports), #110 …". */
const MeasureLinks: React.FC<{ measures: LinkedMeasure[] }> = ({ measures }) => (
  <>
    {measures.map((measure, index) => (
      <React.Fragment key={measure.id}>
        {index > 0 && ', '}
        <Link to={`/admin-next/elections/initiatives/${measure.id}`} className="font-medium underline">
          {measureLabel(measure)}
        </Link>
        {measure.side === 'support' ? ' (supports)' : measure.side === 'oppose' ? ' (opposes)' : ''}
      </React.Fragment>
    ))}
  </>
);
/**
 * The measure's two sides — issue committees (with real person/org links, not
 * display-only text), fundraising rollups, arguments, and endorsements. Every
 * delete confirms first (the legacy page's committee/argument/endorsement
 * deletes fired immediately).
 */
const InitiativeSidesTab: React.FC<Props> = ({ initiative, onChanged }) => {
  const [committeeModal, setCommitteeModal] = useState<'add' | 'edit' | null>(null);
  const [committeeForm, setCommitteeForm] = useState(EMPTY_COMMITTEE_FORM);
  const [committeeFinance, setCommitteeFinance] = useState<SearchOption | null>(null);
  // The committee the link pointed at when the dialog opened; only a change is sent.
  const [initialCommitteeOptionId, setInitialCommitteeOptionId] = useState<string | null>(null);
  // Where the picked committee is already linked, and its purpose (§5.5).
  const [committeeLinks, setCommitteeLinks] = useState<CommitteeLinks | null>(null);
  const [editingSide, setEditingSide] = useState<string | null>(null);
  const [argumentModal, setArgumentModal] = useState(false);
  const [argumentForm, setArgumentForm] = useState(EMPTY_ARGUMENT_FORM);
  // undefined = closed; row null = adding to `side`.
  const [editingEndorsement, setEditingEndorsement] = useState<{ row: InitiativeEndorsement | null; side: string } | undefined>(undefined);
  const [endorsementError, setEndorsementError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const committeeSaveMutation = useMutation({
    mutationFn: () => {
      const coId = financeCoIdOf(committeeFinance);
      const payload = {
        committee_type: committeeForm.committee_type,
        notes: committeeForm.notes.trim() || null,
        ...(coId && committeeFinance?.id !== initialCommitteeOptionId ? { finance_co_id: coId } : {}),
      };
      return committeeModal === 'add'
        ? api.post(`/admin/ballot-initiatives/${initiative.id}/committees`, payload)
        : api.put(`/admin/ballot-initiatives/${initiative.id}/committees/${committeeForm.id}`, payload);
    },
    onSuccess: () => {
      toast.success(committeeModal === 'add' ? 'Committee added' : 'Committee updated');
      setCommitteeModal(null);
      onChanged();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save committee')),
  });

  const argumentSaveMutation = useMutation({
    mutationFn: () =>
      api.post(`/admin/ballot-initiatives/${initiative.id}/arguments`, {
        side: argumentForm.side,
        source_type: argumentForm.source_type,
        title: argumentForm.title.trim() || null,
        body: normalizeRichText(argumentForm.body),
        source_name: argumentForm.source_name.trim() || null,
        source_url: argumentForm.source_url.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Argument added');
      setArgumentModal(false);
      onChanged();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to add argument')),
  });

  // Add or edit through the shared endorsement routes (ENDORSEMENTS_PRD §5.5).
  const endorsementSaveMutation = useMutation({
    mutationFn: (input: EndorsementInput) => {
      const base = `/admin/ballot-initiatives/${initiative.id}/endorsements`;
      const rowId = editingEndorsement?.row?.id;
      return rowId ? api.put(`${base}/${rowId}`, input) : api.post(base, input);
    },
    onSuccess: () => {
      toast.success(editingEndorsement?.row ? 'Endorsement updated' : 'Endorsement added');
      setEditingEndorsement(undefined);
      onChanged();
    },
    onError: (error: any) => setEndorsementError(apiError(error, 'Failed to save the endorsement')),
  });

  const deleteMutation = useMutation({
    mutationFn: (target: DeleteTarget) => {
      const paths: Record<DeleteTarget['kind'], string> = {
        committee: `/admin/ballot-initiatives/${initiative.id}/committees/${target.id}`,
        argument: `/admin/ballot-initiatives/${initiative.id}/arguments/${target.id}`,
        endorsement: `/admin/ballot-initiatives/${initiative.id}/endorsements/${target.id}`,
      };
      return api.delete(paths[target.kind]);
    },
    onSuccess: (_response, target) => {
      toast.success(`${target.kind === 'committee' ? 'Committee' : target.kind === 'argument' ? 'Argument' : 'Endorsement'} removed`);
      setDeleteTarget(null);
      onChanged();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to remove')),
  });

  const loadCommitteeLinks = async (coId: string): Promise<CommitteeLinks | null> => {
    try {
      const response = await api.get('/admin/ballot-initiatives/committee-links', {
        params: { co_id: coId, initiative_id: initiative.id },
      });
      const links = (response.data.data ?? null) as CommitteeLinks | null;
      setCommitteeLinks(links);
      return links;
    } catch {
      // The warnings are advisory; saving still works without them.
      setCommitteeLinks(null);
      return null;
    }
  };

  const openAddCommittee = () => {
    setCommitteeForm(EMPTY_COMMITTEE_FORM);
    setCommitteeFinance(null);
    setInitialCommitteeOptionId(null);
    setCommitteeLinks(null);
    setEditingSide(null);
    setCommitteeModal('add');
  };

  const selectFinanceCommittee = async (option: SearchOption) => {
    setCommitteeFinance(option);
    const coId = financeCoIdOf(option);
    if (!coId) {
      setCommitteeLinks(null);
      return;
    }
    await loadCommitteeLinks(coId);
  };

  /** Edit a link; for an unmatched legacy row ("Match"), the picker opens pre-searched. */
  const openEditCommittee = (committee: InitiativeCommittee) => {
    const matched = Boolean(committee.finance_co_id);
    setCommitteeForm({
      id: committee.id,
      committee_type: committee.committee_type,
      notes: committee.notes ?? '',
      match_name: matched ? '' : committee.committee_name,
    });
    const current = matched
      ? { id: `co:${committee.finance_co_id}`, label: committee.finance_committee_name ?? committee.committee_name }
      : null;
    setCommitteeFinance(current);
    setInitialCommitteeOptionId(current?.id ?? null);
    setCommitteeLinks(null);
    setEditingSide(SIDE_BY_COMMITTEE_TYPE[committee.committee_type] ?? null);
    setCommitteeModal('edit');
    if (committee.finance_co_id) void loadCommitteeLinks(committee.finance_co_id);
  };

  const openAddArgument = (side: string) => {
    setArgumentForm({ ...EMPTY_ARGUMENT_FORM, side });
    setArgumentModal(true);
  };

  const openAddEndorsement = (side: string) => {
    setEndorsementError(null);
    setEditingEndorsement({ row: null, side });
  };

  const openEditEndorsement = (row: InitiativeEndorsement) => {
    setEndorsementError(null);
    setEditingEndorsement({ row, side: row.side });
  };

  const argumentsFor = (side: string) => initiative.arguments.filter((entry) => entry.side === side);
  const endorsementsFor =(side: string) => initiative.endorsements.filter((entry) => entry.side === side);

  const linkWarnings = committeeLinkWarnings(
    committeeLinks,
    SIDE_BY_COMMITTEE_TYPE[committeeForm.committee_type] ?? null,
    editingSide,
  );

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="mb-1 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary-700 dark:text-primary-dark-300" />
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Fundraising</h4>
          <HelpTrigger contentKey="initiative-finance-linkage" size="sm" />
        </div>
        <p className="mb-4 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          Totals from the committees dedicated to this measure, over the election cycle window.
          Committees that also back other measures on this ballot are listed separately.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {INITIATIVE_SIDES.map((side) => {
            const finance: SideFinance | undefined = initiative.finance_by_side?.[side.key];
            const detail = initiative.side_finance_detail?.[side.key];
            const shared = initiative.shared_committees_by_side?.[side.key] ?? [];
            const emptyReason = emptyTotalsReason(finance);
            return (
              <div key={side.key} className="rounded-lg border border-border-light p-4 dark:border-border-dark">
                <div className={`mb-2 text-sm font-semibold ${side.accent}`}>{side.label}</div>
                <div className="mb-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  From committees dedicated to this measure
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Raised</div>
                    <div className="font-medium tabular-nums text-text-primary dark:text-text-dark-primary">
                      {fmtMoney(finance?.total_raised ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Spent</div>
                    <div className="font-medium tabular-nums text-text-primary dark:text-text-dark-primary">
                      {fmtMoney(finance?.total_spent ?? 0)}
                    </div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {sideCountParts(finance).map((part, index) => (
                    <span key={part.text} className={part.warning ? 'text-warning-600 dark:text-warning-400' : undefined}>
                      {index > 0 && ' · '}
                      {part.text}
                    </span>
                  ))}
                </div>

                {(detail?.top_donors?.length ?? 0) > 0 && (
                  <div className="mt-3 border-t border-border-light pt-2 dark:border-border-dark">
                    <div className="mb-1 text-xs font-medium text-text-secondary dark:text-text-dark-secondary">
                      Top donors
                    </div>
                    <ul className="space-y-0.5">
                      {detail?.top_donors.map((donor, i) => (
                        <li key={side.key + '-donor-' + i} className="flex justify-between gap-2 text-xs">
                          <span className="truncate text-text-primary dark:text-text-dark-primary">{donor.name}</span>
                          <span className="shrink-0 tabular-nums text-text-secondary dark:text-text-dark-secondary">
                            {fmtMoney(donor.amount)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(detail?.recent_expenditures?.length ?? 0) > 0 && (
                  <div className="mt-3 border-t border-border-light pt-2 dark:border-border-dark">
                    <div className="mb-1 text-xs font-medium text-text-secondary dark:text-text-dark-secondary">
                      Recent spending
                    </div>
                    <ul className="space-y-0.5">
                      {detail?.recent_expenditures.map((item, i) => (
                        <li key={side.key + '-spend-' + i} className="flex justify-between gap-2 text-xs">
                          <span
                            className="truncate text-text-primary dark:text-text-dark-primary"
                            title={item.purpose ?? undefined}
                          >
                            {item.payee}
                          </span>
                          <span className="shrink-0 tabular-nums text-text-secondary dark:text-text-dark-secondary">
                            {fmtMoney(item.amount)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {shared.length > 0 && (
                  <div className="mt-3 border-t border-border-light pt-2 dark:border-border-dark">
                    <div className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary">
                      Shared committees
                    </div>
                    <p className="mb-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      Not included in the totals above — these committees also back other measures on
                      this ballot, and their money can't be attributed to one measure.
                    </p>
                    <ul className="space-y-2">
                      {shared.map((committee) => (
                        <li key={committee.co_id} className="text-xs">
                          <div className="flex justify-between gap-2">
                            <span className="font-medium text-text-primary dark:text-text-dark-primary">
                              {committee.committee_name}
                            </span>
                            <span className="shrink-0 tabular-nums text-text-secondary dark:text-text-dark-secondary">
                              {fmtMoney(committee.total_raised)} raised / {fmtMoney(committee.total_spent)} spent overall
                            </span>
                          </div>
                          {committee.both_sides && (
                            <p className="text-warning-600 dark:text-warning-400">
                              Linked to both sides of this measure — check its type.
                            </p>
                          )}
                          {committee.also_backs.some((measure) => measure.qualifies) && (
                            <p className="text-text-secondary dark:text-text-dark-secondary">
                              Also backs:{' '}
                              <MeasureLinks measures={committee.also_backs.filter((measure) => measure.qualifies)} />
                            </p>
                          )}
                          {committee.purpose && <PurposeText text={committee.purpose} />}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {emptyReason === 'nothing-linked' && (
                  <p className="mt-3 border-t border-border-light pt-2 text-xs text-warning-600 dark:border-border-dark dark:text-warning-400">
                    No committee on this side is linked to campaign finance, so there is nothing
                    to total. That is missing data, not an absence of money.
                  </p>
                )}
                {emptyReason === 'all-shared' && (
                  <p className="mt-3 border-t border-border-light pt-2 text-xs text-text-secondary dark:border-border-dark dark:text-text-dark-secondary">
                    Every linked committee on this side also backs other measures, so no money can be
                    attributed to this measure alone. See Shared committees above.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card p-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
            Campaign Finance Committees ({initiative.committees.length})
          </h4>
          <Button variant="ghost" size="sm" icon={Plus} onClick={openAddCommittee}>
            Add Committee
          </Button>
        </div>
        {initiative.committees.length === 0 ? (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No committees linked yet.</p>
        ) : (
          <ul className="space-y-3">
            {initiative.committees.map((committee) => (
              <li
                key={committee.id}
                className="flex items-start justify-between gap-2 rounded-lg border border-border-light p-3 dark:border-border-dark"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                      {committee.committee_name}
                    </span>
                    <Chip
                      label={committeeTypeLabel(committee.committee_type)}
                      variant={SIDE_CHIP_VARIANTS[committee.committee_type] ?? 'neutral'}
                      size="xs"
                    />
                    {!committee.finance_co_id && <Chip label="Not matched" variant="warning" size="xs" />}
                  </div>
                  {!committee.finance_co_id && (
                    <p className="text-xs text-warning-700 dark:text-warning-400">
                      Not linked to a committee in the campaign finance import. Match it so its details
                      come from the committee's record.
                    </p>
                  )}
                  {committee.purpose && <PurposeText text={committee.purpose} />}
                  {committee.contact_person_name && (
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      Contact: {committee.contact_person_name}
                      {committee.contact_person_email && ` (${committee.contact_person_email})`}
                    </p>
                  )}
                  {committee.organization_name && (
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                      Org: {committee.organization_name}
                    </p>
                  )}
                  {committee.website_url && (
                    <a
                      href={committee.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400"
                    >
                      Website <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {committee.notes && (
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">{committee.notes}</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                  {committee.finance_co_id ? (
                    <Link
                      to={committeeToolPath(committee.finance_co_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 text-sm text-primary-600 hover:underline dark:text-primary-400"
                    >
                      Edit in Committees <ExternalLink className="h-3 w-3" aria-hidden />
                    </Link>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => openEditCommittee(committee)}>
                      Match
                    </Button>
                  )}
                  <Button
                    variant="ghost" size="sm" icon={Pencil}
                    ariaLabel={`Edit the link to ${committee.committee_name}`}
                    onClick={() => openEditCommittee(committee)}
                  />
                  <Button
                    variant="ghost" size="sm" icon={Trash2}
                    ariaLabel={`Remove ${committee.committee_name}`}
                    onClick={() =>
                      setDeleteTarget({ kind: 'committee', id: committee.id, label: committee.committee_name })
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card p-5">
        <div className="mb-4 flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary-700 dark:text-primary-dark-300" />
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Arguments</h4>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {INITIATIVE_SIDES.map((side) => (
            <div key={side.key}>
              <div className="mb-2 flex items-center justify-between">
                <span className={`text-sm font-semibold ${side.accent}`}>
                  {side.label} ({argumentsFor(side.key).length})
                </span>
                <Button variant="ghost" size="sm" icon={Plus} onClick={() => openAddArgument(side.key)}>
                  Add
                </Button>
              </div>
              {argumentsFor(side.key).length === 0 ? (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No arguments yet.</p>
              ) : (
                <ul className="space-y-2">
                  {argumentsFor(side.key).map((argument) => (
                    <li
                      key={argument.id}
                      className="flex items-start justify-between gap-2 rounded-lg border border-border-light p-3 dark:border-border-dark"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {argument.title && (
                            <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                              {argument.title}
                            </span>
                          )}
                          <Chip label={argument.source_type.replace('_', ' ')} variant="neutral" size="xs" />
                        </div>
                        <RichContent
                          html={argument.body}
                          className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary"
                        />
                        {(argument.source_name || argument.source_url) && (
                          <div className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            {argument.source_name}
                            {argument.source_url && (
                              <>
                                {argument.source_name ? ' · ' : ''}
                                <a
                                  href={argument.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:underline dark:text-primary-400"
                                >
                                  source
                                </a>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost" size="sm" icon={Trash2}
                        ariaLabel="Remove argument"
                        onClick={() =>
                          setDeleteTarget({
                            kind: 'argument',
                            id: argument.id,
                            label: argument.title || `${side.label.toLowerCase()} argument`,
                          })
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card p-5">
        <div className="mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-primary-700 dark:text-primary-dark-300" />
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Endorsements</h4>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {INITIATIVE_SIDES.map((side) => (
            <div key={side.key}>
              <div className="mb-2 flex items-center justify-between">
                <span className={`text-sm font-semibold ${side.accent}`}>
                  {side.label} ({endorsementsFor(side.key).length})
                </span>
                <Button variant="ghost" size="sm" icon={Plus} onClick={() => openAddEndorsement(side.key)}>
                  Add
                </Button>
              </div>
              <EndorsementList
                rows={endorsementsFor(side.key)}
                emptyText="No endorsements yet."
                actionNotes={{ edit: 'Corrects this endorsement.', delete: 'Removes this endorsement after you confirm.' }}
                onEdit={(row) => openEditEndorsement(row)}
                onDelete={(row) => setDeleteTarget({ kind: 'endorsement', id: row.id, label: endorserDisplayName(row) })}
              />
            </div>
          ))}
        </div>
      </div>

      <AdminModal
        isOpen={committeeModal !== null}
        title={committeeModal === 'add' ? 'Link a Committee' : 'Edit Committee Link'}
        onClose={() => setCommitteeModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setCommitteeModal(null)}>Cancel</Button>
            <Button
              variant="primary" size="sm"
              disabled={!financeCoIdOf(committeeFinance)}
              loading={committeeSaveMutation.isLoading}
              onClick={() => committeeSaveMutation.mutate()}
            >
              Save Link
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <span className={labelClasses}>Committee *</span>
            <SearchSelect
              key={`${committeeForm.id || 'new'}-${committeeForm.match_name}`}
              placeholder="Search by name, former name, or TRACER ID…"
              ariaLabel="Search committees in the campaign finance import"
              fetchOptions={searchImportedCommittees}
              selected={committeeFinance}
              initialTerm={committeeForm.match_name || undefined}
              onSelect={(option) => void selectFinanceCommittee(option)}
              onClear={() => {
                setCommitteeFinance(null);
                setCommitteeLinks(null);
              }}
            />
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Committees from the TRACER campaign finance import. Linking one adds its contributions
              and spending to this side. A committee that hasn't filed yet isn't listed until its first
              report is imported.
            </p>
            {linkWarnings.bothSides && (
              <p className="mt-2 flex gap-1.5 text-xs text-warning-600 dark:text-warning-400" role="status">
                <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                This committee is already on the other side of this measure. It will count toward
                neither side's totals until one of the links is corrected.
              </p>
            )}
            {linkWarnings.shared.length > 0 && (
              <p className="mt-2 flex gap-1.5 text-xs text-warning-600 dark:text-warning-400" role="status">
                <WarningIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  Also linked to <MeasureLinks measures={linkWarnings.shared} />. Its money will show as
                  shared on each of these measures and won't count toward their totals.
                </span>
              </p>
            )}
            {linkWarnings.notCounted.length > 0 && (
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Also linked to <MeasureLinks measures={linkWarnings.notCounted} /> — not on this ballot,
                so it doesn't affect the totals.
              </p>
            )}
          </div>
          {financeCoIdOf(committeeFinance) && (
            <div className="rounded-lg border border-border-light p-3 dark:border-border-dark">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary">
                  Purpose statement
                </span>
                <Link
                  to={committeeToolPath(financeCoIdOf(committeeFinance) as string)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline dark:text-primary-400"
                >
                  Edit in Committees <ExternalLink className="h-3 w-3" aria-hidden />
                </Link>
              </div>
              {committeeLinks?.purpose ? (
                <PurposeText text={committeeLinks.purpose} />
              ) : (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  None yet. Add it in Committees, copied from the committee's TRACER registration.
                </p>
              )}
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                The purpose, website, organization and contact belong to the committee, so they're the
                same on every measure it's linked to.
              </p>
            </div>
          )}
          <div>
            <label htmlFor="committee-type" className={labelClasses}>Side</label>
            <select
              id="committee-type"
              value={committeeForm.committee_type}
              onChange={(event) =>
                setCommitteeForm((current) => ({ ...current, committee_type: event.target.value }))
              }
              className={inputClasses}
            >
              {COMMITTEE_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="committee-notes" className={labelClasses}>Notes about this measure</label>
            <textarea
              id="committee-notes"
              rows={2}
              value={committeeForm.notes}
              onChange={(event) => setCommitteeForm((current) => ({ ...current, notes: event.target.value }))}
              className={inputClasses}
            />
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={argumentModal}
        title={`Add ${argumentForm.side === 'support' ? 'Support' : 'Oppose'} Argument`}
        onClose={() => setArgumentModal(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setArgumentModal(false)}>Cancel</Button>
            <Button
              variant="primary" size="sm"
              disabled={richTextIsEmpty(argumentForm.body)}
              loading={argumentSaveMutation.isLoading}
              onClick={() => argumentSaveMutation.mutate()}
            >
              Add Argument
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="argument-source-type" className={labelClasses}>Source Type</label>
              <select
                id="argument-source-type"
                value={argumentForm.source_type}
                onChange={(event) =>
                  setArgumentForm((current) => ({ ...current, source_type: event.target.value }))
                }
                className={inputClasses}
              >
                {ARGUMENT_SOURCE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="argument-title" className={labelClasses}>Title</label>
              <input
                id="argument-title"
                type="text"
                value={argumentForm.title}
                onChange={(event) => setArgumentForm((current) => ({ ...current, title: event.target.value }))}
                className={inputClasses}
              />
            </div>
          </div>
          <div>
            <span className={labelClasses}>Argument Text *</span>
            <QuillEditor
              value={argumentForm.body}
              onChange={(value) => setArgumentForm((current) => ({ ...current, body: value }))}
              placeholder="The argument as made"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="argument-source-name" className={labelClasses}>Source Name</label>
              <input
                id="argument-source-name"
                type="text"
                value={argumentForm.source_name}
                onChange={(event) =>
                  setArgumentForm((current) => ({ ...current, source_name: event.target.value }))
                }
                className={inputClasses}
              />
            </div>
            <div>
              <label htmlFor="argument-source-url" className={labelClasses}>Source URL</label>
              <input
                id="argument-source-url"
                type="url"
                value={argumentForm.source_url}
                onChange={(event) =>
                  setArgumentForm((current) => ({ ...current, source_url: event.target.value }))
                }
                className={inputClasses}
              />
            </div>
          </div>
        </div>
      </AdminModal>

      <EndorsementModal
        isOpen={editingEndorsement !== undefined}
        kind="measure"
        row={editingEndorsement?.row ?? null}
        defaultSide={editingEndorsement?.side}
        saving={endorsementSaveMutation.isLoading}
        error={endorsementError}
        onSave={(input) => endorsementSaveMutation.mutate(input)}
        onClose={() => setEditingEndorsement(undefined)}
      />

      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        title={
          deleteTarget?.kind === 'committee'
            ? 'Remove committee'
            : deleteTarget?.kind === 'argument'
              ? 'Remove argument'
              : 'Remove endorsement'
        }
        message={deleteTarget ? `Remove "${deleteTarget.label}"? This cannot be undone.` : ''}
        confirmText="Remove"
        variant="danger"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default InitiativeSidesTab;
